import React from 'react';
import { FiPaperclip, FiX } from 'react-icons/fi';
import { useQuery } from '@tanstack/react-query';
import { getProductNamesAll } from '../../api/products';
import FormField from '../../components/ui/FormField';
import { Input, Select, Textarea, NumberInput } from '../../components/ui/Input';
import FileUploader from '../../components/ui/FileUploader';
import VendorPicker from '../../components/shared/VendorPicker';
import { DEPARTMENTS } from '../../auth/permissions';
import { RecurringType, enumOptions } from '../../enums';

export const CATEGORY_OPTIONS = [
  'TRAVEL',
  'MEALS',
  'OFFICE_SUPPLIES',
  'SOFTWARE',
  'HARDWARE',
  'TRAINING',
  'UTILITIES',
  'MARKETING',
  'CONSULTING',
  'SALARY',
  'SUBSCRIPTION',
  'OTHER',
];

export const EXPENSE_TYPE_OPTIONS = [
  'REIMBURSEMENT',
  'COMPANY EXPENSE'
];

export const PAYMENT_TYPE_OPTIONS = [
  'CREDIT CARD',
  'DEBIT CARD',
  'NET BANKING',
  'BANK TRANSFER',
  'UPI',
  'CASH'
];

export const DEPARTMENT_OPTIONS = Object.values(DEPARTMENTS);

export default function ExpenseFormInner({ form, update, errors, file, setFile, isEdit = false }) {
  const { data: products = [] } = useQuery({
    queryKey: ['products', 'simple'],
    queryFn: getProductNamesAll,
  });

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <FormField label="Title" required error={errors.title} className="md:col-span-2">
        <Input
          value={form.title}
          onChange={(e) => update({ title: e.target.value })}
          placeholder="Brief subject line, e.g. Q3 vendor onsite travel"
          error={!!errors.title}
          autoFocus
        />
      </FormField>

      <FormField label="Description" className="md:col-span-2" hint="Optional context for the approver.">
        <Textarea
          rows={3}
          value={form.description || ''}
          onChange={(e) => update({ description: e.target.value })}
          placeholder="What is this expense for?"
        />
      </FormField>

      <FormField label="Amount (INR)" required error={errors.amount}>
        <NumberInput
          min="0"
          step="0.01"
          value={form.amount}
          onChange={(e) => update({ amount: e.target.value })}
          error={!!errors.amount}
          placeholder="0.00"
        />
      </FormField>

      <FormField label="Transaction Date" hint="Optional — Date of the transaction.">
        <Input
          type="date"
          value={form.transactionDate || ''}
          onChange={(e) => update({ transactionDate: e.target.value })}
        />
      </FormField>

      <FormField label="Bank Description" hint="Optional — As it appears on the bank statement." className="md:col-span-2">
        <Input
          value={form.bankDescription || ''}
          onChange={(e) => update({ bankDescription: e.target.value })}
          placeholder="e.g. POS DEBIT 1234"
        />
      </FormField>

      <FormField label="Comments" hint="Optional — Additional notes." className="md:col-span-2">
        <Textarea
          rows={2}
          value={form.comments || ''}
          onChange={(e) => update({ comments: e.target.value })}
          placeholder="Any extra details..."
        />
      </FormField>

      <FormField label="Category">
        <Select
          value={form.category}
          onChange={(e) => update({ category: e.target.value })}
          error={!!errors.category}
        >
          <option value="">Select category…</option>
          {CATEGORY_OPTIONS.map((c) => (
            <option key={c} value={c}>
              {c.replace(/_/g, ' ')}
            </option>
          ))}
        </Select>
      </FormField>

      <FormField label="Expense Type">
        <Select
          value={form.expenseType || ''}
          onChange={(e) => update({ expenseType: e.target.value })}
          error={!!errors.expenseType}
        >
          <option value="">Select expense type…</option>
          {EXPENSE_TYPE_OPTIONS.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </Select>
      </FormField>

      <FormField label="Payment Type">
        <Select
          value={form.paymentType || ''}
          onChange={(e) => update({ paymentType: e.target.value })}
          error={!!errors.paymentType}
        >
          <option value="">Select payment type…</option>
          {PAYMENT_TYPE_OPTIONS.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </Select>
      </FormField>

      <FormField label="Department">
        <Select
          value={form.department}
          onChange={(e) => update({ department: e.target.value })}
          error={!!errors.department}
        >
          <option value="">Select department…</option>
          {DEPARTMENT_OPTIONS.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </Select>
      </FormField>

      <FormField label="Vendor" hint="Optional — link to a vendor for reporting.">
        <VendorPicker
          value={form.vendorId}
          onChange={(id) => update({ vendorId: id })}
        />
      </FormField>

      <FormField label="Product">
        <Select
          value={form.productId || ''}
          onChange={(e) => update({ productId: e.target.value ? Number(e.target.value) : '' })}
          error={!!errors.productId}
        >
          <option value="">Select product…</option>
          {products.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </Select>
      </FormField>

      <div className="md:col-span-2 rounded-md border border-slate-200 bg-slate-50/60 px-4 py-3">
        <label className="flex items-start gap-3 text-sm">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary/30"
            checked={form.isRecurring}
            onChange={(e) => update({ isRecurring: e.target.checked })}
          />
          <span>
            <span className="font-medium text-ink">Recurring expense</span>
            <span className="ml-2 text-xs text-ink-muted">
              The same amount will be filed automatically on a cadence.
            </span>
          </span>
        </label>

        {form.isRecurring && (
          <div className="mt-3 max-w-xs">
            <FormField label="Cadence" required error={errors.recurringType}>
              <Select
                value={form.recurringType || ''}
                onChange={(e) => update({ recurringType: e.target.value })}
                error={!!errors.recurringType}
              >
                <option value="">Select cadence…</option>
                {enumOptions(RecurringType).map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </FormField>
          </div>
        )}
      </div>

      {!isEdit && (
        <div className="md:col-span-2">
          <FormField
            label="Attachment"
            hint="Optional — receipt, invoice, or supporting document (max 10MB)."
          >
            {file ? (
              <div className="flex items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-2">
                <div className="flex min-w-0 items-center gap-2 text-sm">
                  <FiPaperclip className="text-ink-muted" />
                  <span className="truncate text-ink">{file.name}</span>
                  <span className="shrink-0 text-xs text-ink-muted">
                    {(file.size / 1024).toFixed(1)} KB
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setFile(null)}
                  className="rounded p-1 text-ink-muted hover:bg-slate-100 hover:text-danger"
                  aria-label="Remove attachment"
                >
                  <FiX size={16} />
                </button>
              </div>
            ) : (
              <FileUploader
                variant="dropzone"
                onSelect={(f) => setFile(f)}
              />
            )}
          </FormField>
        </div>
      )}
    </div>
  );
}
